import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiChevronDown } from "react-icons/fi";
import { get as commsGet, put as commsPut } from "../../../service/comms/commsApi";
import { getAgentPresence, setAgentPresence } from "../../../service/comms/supportService";
import { AGENT_PRESENCE, DEPARTMENTS } from "./supportEnums";

/** Dot colour per declared availability. Full class strings (Tailwind-safe). */
const DOT_CLASSES = {
  available: "bg-success",
  away: "bg-orange-primary",
  offline: "bg-gray-400",
};

/** Fallback when the agent has never set one. */
const DEFAULT_CAPACITY = 10;

/**
 * The agent's own availability control — status, the queues they take, and how
 * many open tickets they'll accept.
 *
 * All three are what auto-assign actually routes on. Round-robin sends a new
 * ticket to "the next `available` agent in that department's pool who is under
 * `capacity`", and pool membership comes from **this** row: an agent declares
 * which departments they cover. A manager turning round-robin on for Billing
 * achieves nothing until somebody has claimed Billing here.
 *
 * `PUT /agents/me/presence` only ever writes the caller's own row, so this is
 * the only place any of it can be set — there is no manager-side endpoint for
 * putting somebody else in a pool.
 *
 * Omitted fields are left untouched server-side, so the quick status change
 * sends `{ status }` alone and never clobbers a capacity the agent set earlier.
 * `company_id` comes back on the row but is never sent (see `setAgentPresence`).
 *
 * @param {Object} props
 * @param {(presence: Object) => void} [props.onChange] - Notified after a save.
 * @param {'light'|'dark'} [props.variant='light'] - `dark` for the ops bar.
 */
export function AgentPresenceToggle({ onChange, variant = "light" }) {
  const [presence, setPresence] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [open, setOpen] = useState(false);
  // Local edits, committed on Save so ticking three departments is one request.
  const [draft, setDraft] = useState({ departments: [], capacity: DEFAULT_CAPACITY });
  const ref = useRef(null);

  const applyPresence = useCallback((row) => {
    setPresence({
      status: row?.status || "offline",
      capacity: row?.capacity ?? null,
      departments: Array.isArray(row?.departments) ? row.departments : [],
      // v1.9.1 tenant boundary — null means no routing pool at all.
      companyId: row?.company_id ?? null,
    });
  }, []);

  useEffect(() => {
    let active = true;
    getAgentPresence(commsGet)
      .then((row) => active && applyPresence(row))
      .catch((err) => {
        console.error("Failed to load agent presence:", err);
        // Non-agents (and pre-v1.9 backends) simply don't get the control.
        if (active) setUnavailable(true);
      });
    return () => {
      active = false;
    };
  }, [applyPresence]);

  // Seed the draft each time the menu opens, so a cancelled edit is discarded.
  useEffect(() => {
    if (open && presence) {
      setDraft({
        departments: presence.departments,
        capacity: presence.capacity ?? DEFAULT_CAPACITY,
      });
    }
  }, [open, presence]);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  /**
   * @param {Object} patch - Only the fields being changed; the rest are left
   *   alone server-side.
   */
  const save = async (patch) => {
    const previous = presence;
    setPresence((p) => ({ ...p, ...patch, departments: patch.departments ?? p.departments }));
    setSaving(true);
    try {
      const row = await setAgentPresence(commsPut, patch);
      applyPresence(row);
      onChange?.(row);
    } catch (err) {
      console.error("Failed to update agent presence:", err);
      setPresence(previous);
    } finally {
      setSaving(false);
    }
  };

  const toggleDraftDepartment = (department) =>
    setDraft((d) => ({
      ...d,
      departments: d.departments.includes(department)
        ? d.departments.filter((x) => x !== department)
        : [...d.departments, department],
    }));

  const saveDraft = async () => {
    await save({
      status: presence.status,
      departments: draft.departments,
      capacity: Number(draft.capacity) || DEFAULT_CAPACITY,
    });
    setOpen(false);
  };

  if (unavailable || presence === null) return null;

  const { status, departments, companyId } = presence;
  const isDark = variant === "dark";
  const inRoutingPool = Boolean(companyId);
  // On the dark ops bar an "available" agent reads as a green-tinted pill;
  // anything else stays neutral so only the good state draws the eye.
  const shellClass = isDark
    ? status === "available"
      ? "bg-success/20 text-emerald-300"
      : "bg-white/10 text-gray-200"
    : "border border-gray-200 bg-white text-gray-700 shadow-sm";

  const summary =
    status === "available" && departments.length > 0
      ? `Available · ${departments.length} queue${departments.length === 1 ? "" : "s"}`
      : AGENT_PRESENCE.find((p) => p.key === status)?.label || "Offline";

  return (
    <div ref={ref} className="relative flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${shellClass}`}
      >
        <span
          aria-hidden="true"
          className={`h-1.5 w-1.5 rounded-full ${DOT_CLASSES[status] || DOT_CLASSES.offline}`}
        />
        {summary}
        <FiChevronDown aria-hidden="true" size={12} />
      </button>

      {/* Without this the failure is completely silent: the agent looks
          Available, sees the queue, and simply never gets routed anything. */}
      {!inRoutingPool && (
        <button
          type="button"
          onClick={() => save({ status })}
          disabled={saving}
          title="Your presence row has no company, so round-robin skips you and you get no new-ticket alerts. Click to re-resolve it."
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-60 ${
            isDark
              ? "bg-danger/25 text-error-red-light hover:bg-danger/35"
              : "bg-danger-bg text-danger-dark hover:brightness-95"
          }`}
        >
          <FiAlertTriangle aria-hidden="true" size={11} />
          {saving ? "Rejoining…" : "Not in routing pool"}
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="Your availability"
          className="absolute right-0 top-full z-30 mt-1.5 w-72 rounded-lg border border-gray-200 bg-white p-3 text-gray-800 shadow-xl"
        >
          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-1.5 p-0 text-[10px] font-bold uppercase tracking-wide text-gray-500">
              Availability
            </legend>
            <div className="flex gap-1">
              {AGENT_PRESENCE.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  disabled={saving}
                  onClick={() => save({ status: p.key })}
                  aria-pressed={status === p.key}
                  className={`flex-1 rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-60 ${
                    status === p.key
                      ? "border-orange-primary bg-light-orange text-orange-primary"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="m-0 mt-3 border-0 p-0">
            <legend className="mb-1.5 p-0 text-[10px] font-bold uppercase tracking-wide text-gray-500">
              Queues you take
            </legend>
            <div className="grid grid-cols-2 gap-1">
              {DEPARTMENTS.map((d) => (
                <label
                  key={d}
                  className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={draft.departments.includes(d)}
                    onChange={() => toggleDraftDepartment(d)}
                    className="h-3.5 w-3.5 accent-orange-primary"
                  />
                  {d}
                </label>
              ))}
            </div>
            <p className="m-0 mt-1 text-[10px] leading-snug text-gray-400">
              Auto-assign only routes you tickets from the queues you pick here.
            </p>
          </fieldset>

          <label className="mt-3 block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-gray-500">
              Capacity
            </span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={99}
                value={draft.capacity}
                onChange={(e) => setDraft((d) => ({ ...d, capacity: e.target.value }))}
                className="w-16 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
              />
              <span className="text-[10px] leading-snug text-gray-400">
                Round-robin skips you past this many open tickets.
              </span>
            </div>
          </label>

          <div className="mt-3 flex justify-end gap-2 border-t border-gray-100 pt-2.5">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md border border-gray-200 px-2.5 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveDraft}
              disabled={saving}
              className="rounded-md bg-orange-primary px-2.5 py-1 text-[11px] font-semibold text-white hover:brightness-95 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentPresenceToggle;
